import { motion } from "framer-motion";
import { Mic, UserCheck, GitMerge, Sparkles, Share2, Lock, Rocket } from "lucide-react";
import { Header } from "@/pages/Operations";

const CONFIG = {
  "voice-dubbing": {
    icon: Mic, eyebrow: "Voice & Dubbing Lab · HD Neural", title: "Advanced Voice Lab",
    subtitle: "OpenAI HD text-to-speech, auto-translation dubbing and Whisper subtitle generation. Voice cloning and lip-sync are NOT_IMPLEMENTED.",
    features: ["OpenAI TTS · 9 voices", "Auto-dubbing · 22+ languages", "Whisper transcription", "SRT subtitle export", "Burn-in captions (ffmpeg)", "Voice cloning · NOT_IMPLEMENTED"],
  },
  avatars: {
    icon: UserCheck, eyebrow: "Avatar Studio · 4K Motion", title: "AI Presenter Studio",
    subtitle: "Generate photoreal AI presenters and avatars driven by your script and voice.",
    features: ["4K photoreal avatars", "Gesture & expression control", "Custom brand avatars", "Multi-camera framing", "Green-screen export", "Real-time preview"],
  },
  workflows: {
    icon: GitMerge, eyebrow: "Workflow Studio · Automation Runtime", title: "Automation Runtime",
    subtitle: "Build and execute multi-step automated workflows chaining engines together.",
    features: ["Visual node editor", "Trigger + schedule runtime", "Conditional branching", "Engine chaining", "Webhook actions", "Run history & replay"],
  },
  quality: {
    icon: Sparkles, eyebrow: "Quality Studio · Engine Gate", title: "Automated QA Gate",
    subtitle: "Automated quality assurance that flags issues and validates outputs before delivery.",
    features: ["Output validation rules", "Artifact detection", "Brand-safety scan", "Auto-retry on fail", "Approval workflows", "Quality scorecards"],
  },
  distribution: {
    icon: Share2, eyebrow: "Distribution Hub · Multi-Channel", title: "Distribution Hub",
    subtitle: "Publish generated content directly to external platforms via social OAuth.",
    features: ["YouTube / TikTok / Reels", "Scheduled publishing", "Multi-account OAuth", "Per-platform reformatting", "UTM & tracking", "Post analytics"],
  },
  security: {
    icon: Lock, eyebrow: "Security Center · SOC2", title: "Security Center",
    subtitle: "Global security policies, threat monitoring and system hardening.",
    features: ["Threat monitoring", "Audit logs", "API key vault", "IP allowlisting", "Session policies", "Anomaly alerts"],
  },
};

export default function Placeholder({ pageKey }) {
  const cfg = CONFIG[pageKey] || CONFIG.workflows;
  return (
    <div data-testid={`placeholder-${pageKey}`} className="p-5 sm:p-8 max-w-[1200px] mx-auto">
      <Header icon={cfg.icon} eyebrow={cfg.eyebrow} title={cfg.title} subtitle={cfg.subtitle} />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {cfg.features.map((f, i) => (
          <motion.div
            key={f}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.05 }}
            className="glass rounded-2xl p-5 hover:border-purple-500/40 transition-colors"
          >
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-600/30 to-cyan-500/20 flex items-center justify-center mb-3">
              <Sparkles size={16} className="text-cyan-300" />
            </div>
            <div className="text-sm font-medium">{f}</div>
          </motion.div>
        ))}
      </div>

      <div className="glass rounded-2xl p-8 text-center grain relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="relative">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-600 to-cyan-500 flex items-center justify-center mx-auto mb-4 pulse-glow">
            <Rocket size={24} className="text-white" />
          </div>
          <h3 className="font-display text-xl font-bold">Module wiring in progress</h3>
          <p className="text-slate-400 text-sm mt-2 max-w-md mx-auto">
            The interface and controls are live. Deep engine execution for this studio activates next —
            tell Vectra Copilot which capability to turn on first.
          </p>
        </div>
      </div>
    </div>
  );
}
